import '../styles/globals.scss'
import { AbstractIntlMessages } from 'next-intl'
import App, { AppContext, AppProps } from 'next/app'
import { DehydratedState, Hydrate, QueryClient } from 'react-query'
import Head from 'next/head'
import React from 'react'
import initBrowserMetrics from 'nextjs-tools/metrics/metricsBrowser'
import { envEnum, getEnv } from 'utils/crossEnv'
import { getXHost } from 'utils/getXHost'
import { getLocale } from 'utils/getLocale'
import { parseUser } from '../utils/parseUser'
import { getGuestKey } from '../utils/getGuestKey'
import { IUser, Languages } from '../interfaces/common'
import { NextIntlProvider } from '../components/LogonFormComponent/components/NextIntlProvider/NextIntlProvider'
import { LanguageContextProvider } from '../components/LanguageContext/LanguageContext'
import { LocaleContextProvider } from '../components/LocaleContext/LocaleContext'
import mainAxiosInstance from '../dal/mainAxiosInstance'
import { prepareHeaders } from '../dal/modules'
import { isBrowser } from '../utils'
import { AppContainer } from '../components/AppContainer/AppContainer'
import { AuthUserDataContextProvider } from '../components/AuthUserDataContext/AuthUserDataContext'
import { UserContextProvider } from '../components/UserContext/UserContext'
import { GuestKeyContextProvider } from '../components/GuestKeyContext/GuestKeyContext'
import { QueryClientProviderContainer } from '../components/QueryClientProviderContainer/QueryClientProviderContainer'
import { Providers } from '../Providers'

interface IProps {
  dehydratedState?: DehydratedState
  serverQueryClient?: QueryClient
  locale: Languages
  user: IUser
  guestKey: string
  messages: AbstractIntlMessages
}

const MyApp = ({
  Component,
  pageProps,
  dehydratedState,
  serverQueryClient,
  locale,
  user,
  guestKey,
}: AppProps & IProps) => {
  initBrowserMetrics({
    env: getEnv(envEnum.NEXT_PUBLIC_ENVIRONMENT) as string,
    PRODUCT_NAME: process.env.NEXT_PUBLIC_PRODUCT_NAME as string,
    SERVICE_NAME: process.env.NEXT_PUBLIC_SERVICE_NAME as string,
  })

  return (
    <Providers>
      <AuthUserDataContextProvider>
        <UserContextProvider defaultValue={user}>
          <GuestKeyContextProvider defaultValue={guestKey}>
            <LocaleContextProvider defaultValue={locale}>
              <QueryClientProviderContainer serverQueryClient={serverQueryClient}>
                <LanguageContextProvider locale={locale}>
                  <Hydrate state={dehydratedState}>
                    <Head>
                      <meta
                        name="viewport"
                        content="width=device-width, height=device-height, initial-scale=1.0, minimum-scale=1.0"
                      />
                      <meta name="robots" content="noindex, nofollow" />
                      <link rel="stylesheet" href="https://cdn.action-mcfr.ru/ajax/libs/meyer-reset/2.0/reset.css" />
                      <link rel="icon" href="/favicon.ico" type="image/x-icon" />
                    </Head>
                    <NextIntlProvider>
                      <AppContainer>
                        <Component {...pageProps} />
                      </AppContainer>
                    </NextIntlProvider>
                  </Hydrate>
                </LanguageContextProvider>
              </QueryClientProviderContainer>
            </LocaleContextProvider>
          </GuestKeyContextProvider>
        </UserContextProvider>
      </AuthUserDataContextProvider>
    </Providers>
  )
}

// SSR
MyApp.getInitialProps = async function getInitialProps(appContext: AppContext) {
  const appProps = await App.getInitialProps(appContext)

  if (isBrowser || appProps.pageProps?.statusCode === 404) {
    return appProps
  }

  const headers = appContext.ctx.req?.headers

  mainAxiosInstance.defaults.headers.common = prepareHeaders(headers)

  const getDataFromTree = (await import('../server/getDataFromTree')).default

  const xHostHeader = getXHost(headers)

  const locale = getLocale(xHostHeader)

  const user = parseUser(headers)

  const guestKey = getGuestKey(headers)

  const data = await getDataFromTree(appContext, {
    ...appProps,
    locale,
    user,
    guestKey,
  })

  // @ts-ignore
  return { ...data }
}

export default MyApp
